import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserAuthService } from '../UserAuth.service';
import {
  CureSheet,
  CureSheetSaveResponse,
  CureSheetMeta,
  CureSheetApprovalAuditResponse,
  CureSheetApprovalStatus,
  CureSheetUpdatePatch,
  EffectiveCureSheetsResponse,
  ExtractionResult,
} from './cure-sheets.types';

/**
 * Client for the Enhanced Cure Sheets API (VericureDataServices).
 * Mirrors the PreferencesService pattern: environment.apiUrl with
 * tenantId/objectId/customerId from UserAuthService. There is no function `code`
 * key — it was removed from the environments because it shipped in the bundle and
 * authenticated nobody; VericureAuthInterceptor attaches the bearer instead.
 */
@Injectable({
  providedIn: 'root',
})
export class CureSheetsService {
  /** Cached effective sheets for the current user (end-user views). */
  effective$ = new BehaviorSubject<EffectiveCureSheetsResponse | null>(null);

  private effectiveLoaded = false;

  constructor(
    private http: HttpClient,
    private userAuth: UserAuthService,
  ) {}

  private get baseUrl(): string {
    return `${environment.apiUrl}v1/cure-sheets`;
  }

  private baseParams(): HttpParams {
    let params = new HttpParams().set('objectId', this.userAuth.getObjectId());

    // Company scope, using the same accessors getEffectiveSheets already uses.
    // The content endpoint needs it to resolve an ORDINARY user's exposure; an
    // admin is answered without it, and it is harmless on the other calls.
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    if (tenantId) params = params.set('tenantId', tenantId);
    const customerId = this.userAuth.getActiveCustomerId();
    if (customerId) params = params.set('customerId', customerId);

    return params;
  }

  // ── Library (admin) ─────────────────────────────────────────────────────────

  /**
   * The library. Every caller gets the APPROVED versions of each sheet, because
   * choosing which version to expose to operators is impossible without them.
   * `includeVersions` additionally returns the full history — drafts, rejections
   * and reviewer notes — and the server honours it for SuperAdmins only.
   */
  listLibrary(opts?: { includeArchived?: boolean; includeVersions?: boolean }): Observable<CureSheet[]> {
    let params = this.baseParams();
    if (opts?.includeArchived) params = params.set('includeArchived', 'true');
    if (opts?.includeVersions) params = params.set('includeVersions', 'true');
    return this.http.get<CureSheet[]>(this.baseUrl, { params });
  }

  getSheet(id: string): Observable<CureSheet> {
    return this.http.get<CureSheet>(`${this.baseUrl}/${id}`, { params: this.baseParams() });
  }

  createSheet(file: File, meta: CureSheetMeta, brandName: string, description: string): Observable<CureSheetSaveResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('meta', JSON.stringify(meta));
    formData.append('brandName', brandName);
    formData.append('description', description);
    return this.libraryWrite(this.http.post<CureSheetSaveResponse>(this.baseUrl, formData, { params: this.baseParams() }));
  }

  addVersion(id: string, file: File, meta: CureSheetMeta, notes: string): Observable<CureSheetSaveResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('meta', JSON.stringify(meta));
    formData.append('notes', notes);
    return this.libraryWrite(
      this.http.post<CureSheetSaveResponse>(`${this.baseUrl}/${id}/versions`, formData, { params: this.baseParams() })
    );
  }

  /** Runs the server-side extractor without persisting — used to pre-fill the form. */
  extract(file: File): Observable<ExtractionResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ExtractionResult>(`${this.baseUrl}/extract`, formData, { params: this.baseParams() });
  }

  updateSheet(id: string, patch: CureSheetUpdatePatch): Observable<CureSheet> {
    return this.libraryWrite(this.http.put<CureSheet>(`${this.baseUrl}/${id}`, patch, { params: this.baseParams() }));
  }

  /** Soft delete — the sheet is archived, not removed. */
  /**
   * Records a review decision against ONE version. SuperAdmin only, server-side.
   * Until a version is approved no device is served it, whatever a customer has
   * enabled — so this is the step that actually publishes.
   */
  setVersionApproval(
    id: string,
    version: number,
    status: CureSheetApprovalStatus,
    notes: string,
  ): Observable<CureSheet> {
    return this.libraryWrite(
      this.http.post<CureSheet>(
        `${this.baseUrl}/${id}/versions/${version}/approval`,
        { status, notes },
        { params: this.baseParams() },
      )
    );
  }

  /**
   * Fetches one version's PDF bytes through the API.
   *
   * WHY A BLOB AND NOT AN <a href>. The endpoint requires a validated bearer, and
   * VericureAuthInterceptor is an Angular HttpInterceptor — it only sees HttpClient
   * traffic. A browser navigation from an anchor bypasses it entirely and would
   * arrive unauthenticated, so a plain link to this endpoint 401s. Fetching through
   * HttpClient is what puts the token on the request.
   *
   * The stored blob `url` is deliberately NOT used: it is a long-lived SAS whose
   * possession outlives approval withdrawal, and this endpoint re-derives access
   * per request.
   *
   * A refusal's body arrives as a Blob too (the request asked for one), so `err.error.message`
   * would never be there. It is parsed back to JSON before the error reaches the caller, so a
   * caller can show the server's reason (DS e28a81e: 404 "This cure sheet is not available.").
   */
  getVersionPdf(id: string, version: number): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/${id}/versions/${version}/content`, {
        params: this.baseParams(),
        responseType: 'blob',
      })
      .pipe(catchError((err: unknown) => withJsonErrorBody(err)));
  }

  /**
   * The review audit across the whole library. SuperAdmin only server-side — it
   * names who decided what, which is Vortex's internal control and not a
   * customer's business.
   */
  approvalAudit(filters: {
    status?: string;
    approver?: string;
    from?: string;
    to?: string;
  } = {}): Observable<CureSheetApprovalAuditResponse> {
    let params = this.baseParams();
    if (filters.status) params = params.set('status', filters.status);
    if (filters.approver) params = params.set('approver', filters.approver);
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    return this.http.get<CureSheetApprovalAuditResponse>(`${this.baseUrl}/approvals`, { params });
  }

  archiveSheet(id: string): Observable<CureSheet> {
    return this.libraryWrite(this.http.delete<CureSheet>(`${this.baseUrl}/${id}`, { params: this.baseParams() }));
  }

  restoreSheet(id: string): Observable<CureSheet> {
    return this.updateSheet(id, { status: 'active' });
  }

  // ── Effective view (any user) ───────────────────────────────────────────────

  /**
   * Sheets effective for the current user (company enabled ∩ active library,
   * user default applied). Cached in effective$; pass force=true to reload.
   */
  getEffectiveSheets(force = false): Observable<EffectiveCureSheetsResponse | null> {
    if (this.effectiveLoaded && !force) {
      return of(this.effective$.value);
    }

    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.userAuth.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();
    if (!tenantId || !objectId) {
      return of(null);
    }

    // No function key: these routes are host-Anonymous and authenticated by the
    // bearer the MSAL interceptor attaches. environment.apiKey no longer exists.
    let params = new HttpParams()
      .set('tenantId', tenantId)
      .set('objectId', objectId);
    if (customerId) {
      params = params.set('customerId', customerId);
    }

    return this.http
      .get<EffectiveCureSheetsResponse>(`${this.baseUrl}/effective`, { params })
      .pipe(
        tap((response) => {
          this.effective$.next(response);
          this.effectiveLoaded = true;
        }),
        catchError((err) => {
          console.warn('CureSheetsService > getEffectiveSheets failed', err);
          return of(this.effective$.value);
        })
      );
  }

  refreshEffective(): void {
    this.effectiveLoaded = false;
    this.getEffectiveSheets(true).subscribe();
  }

  /**
   * A successful LIBRARY write (create, new version, archive/restore, approval)
   * can change which sheets are effective for this user, so the cached effective
   * list must not outlive it (admin-console stale-list audit, 2026-10-02). Already
   * loaded: re-fetch now, so a mounted list or picker updates. Never loaded:
   * nothing to refresh; the next read fetches.
   */
  private libraryWrite<T>(write: Observable<T>): Observable<T> {
    return write.pipe(
      tap(() => {
        if (this.effectiveLoaded) this.refreshEffective();
      })
    );
  }
}

/**
 * Re-throws a blob-bodied HttpErrorResponse with its body parsed as JSON. Anything that is not
 * JSON (or not a Blob) is re-thrown unchanged, so a caller's fallback still applies.
 */
export function withJsonErrorBody(err: unknown): Observable<never> {
  if (!(err instanceof HttpErrorResponse) || !(err.error instanceof Blob)) return throwError(() => err);
  return from(err.error.text().catch(() => '')).pipe(
    switchMap((text) => {
      let body: unknown;
      try {
        body = JSON.parse(text);
      } catch {
        return throwError(() => err);
      }
      return throwError(
        () =>
          new HttpErrorResponse({
            error: body,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined,
          })
      );
    })
  );
}

/** The server's own reason for refusing, when it sent one; otherwise `fallback`. */
export function refusalMessage(err: unknown, fallback: string): string {
  const message = err instanceof HttpErrorResponse ? err.error?.message : undefined;
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
}
